import { InternalId } from '@edgebox/data-definition-kit';
import { MonitoringPromptType, SyncCoreDataDefinitionsEnumTranslator, TaxonomyFacet } from '@edgebox/sync-core-data-definitions';
import {
  ClientMonitoringPromptEntity,
  ClientTaxonomyTermEntity,
  ISyncCoreApi,
  MonitoringPromptTermAssociationOperation,
  MonitoringPromptTermAssociationType,
} from '@edgebox/sync-core-rest-client';
import React from 'react';
import { MonitoringPromptPickerModal } from './MonitoringPromptPickerModal';
import { TaxonomyTermNameBadge } from '../Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import {
  associationListFilter,
  associationPromptEligibility,
  associationSection,
  ITermPromptAssociationSection,
  NON_PREVIOUS_PROMPT_STATUSES,
  summarizeAssociationResults,
  TERM_PROMPT_ASSOCIATION_SECTIONS,
} from './termPromptAssociations.utils';
import { BP_PRIMARY_BUTTON_CLASSES } from './brandPresenceClasses';

const PROMPTS_PER_SECTION = 100;

interface ISectionState {
  prompts: ClientMonitoringPromptEntity[];
  loading: boolean;
  error?: string;
  message?: string;
  busyPromptId?: string;
}

const EMPTY_SECTION: ISectionState = { prompts: [], loading: true };

export interface IGovernanceTermPromptsPanelProps {
  api: ISyncCoreApi;
  term: ClientTaxonomyTermEntity;
  /**
   * The sections this term's facet can appear in — see `termPromptSections`.
   *
   * Absent means every section, which is what a caller that has not worked out the facet's sections
   * wants. A caller that has worked them out passes them, and the panel neither renders nor queries
   * the rest: a section a facet can never appear in is four requests and an empty panel.
   */
  sections?: readonly ITermPromptAssociationSection[];
}

/**
 * The Prompts tab of a taxonomy term: which monitoring prompts use this term, split by the field
 * the association lives in, and editable in place.
 *
 * The four sections are separate because the fields mean different things — one classifies the
 * prompt, one decides what attribution tracks, one is stamped onto runs, one generates instances.
 * Collapsing them into a single "prompts" list would make an operator unable to tell what a term is
 * actually doing.
 */
export function GovernanceTermPromptsPanel({ api, term, sections: facetSections }: IGovernanceTermPromptsPanelProps) {
  const termKey = String(term.key || '');
  const termId = String(term.id || '');
  // What a template is allowed to substitute this term into is decided by the term's own facet, so
  // the picker cannot work it out on its own.
  const termFacet = term.taxonomy?.facet;

  const shownSections = facetSections ?? TERM_PROMPT_ASSOCIATION_SECTIONS;
  // The types the load effect runs over, held by value rather than by array identity: a caller
  // passing its sections inline would otherwise hand this a fresh array on every render, and the
  // effect would refire its requests forever.
  const shownSectionTypesKey = shownSections.map((section) => section.type).join(',');
  const shownSectionTypes = React.useMemo(
    () => shownSectionTypesKey.split(',').filter(Boolean) as MonitoringPromptTermAssociationType[],
    [shownSectionTypesKey]
  );

  const [sections, setSections] = React.useState<Record<string, ISectionState>>({});
  const [instancesByTemplateKey, setInstancesByTemplateKey] = React.useState<Record<string, ClientMonitoringPromptEntity[]>>({});
  const [pickerType, setPickerType] = React.useState<MonitoringPromptTermAssociationType | undefined>(undefined);
  const [pickerSaving, setPickerSaving] = React.useState(false);
  const [pickerError, setPickerError] = React.useState<string | undefined>(undefined);

  const setSection = React.useCallback((type: MonitoringPromptTermAssociationType, update: Partial<ISectionState>) => {
    setSections((current) => ({ ...current, [type]: { ...EMPTY_SECTION, ...current[type], ...update } }));
  }, []);

  const loadSection = React.useCallback(
    async (type: MonitoringPromptTermAssociationType) => {
      if (!termKey) {
        return;
      }

      setSection(type, { loading: true, error: undefined });

      try {
        const result = await api.governance.monitoringPrompts.list(
          { page: 0, itemsPerPage: PROMPTS_PER_SECTION },
          associationListFilter(type, termKey)
        );

        setSection(type, { prompts: result.items || [], loading: false });
      } catch (requestError) {
        setSection(type, { loading: false, error: (requestError as Error)?.message || 'Failed to load prompts.' });
      }
    },
    [api, setSection, termKey]
  );

  React.useEffect(() => {
    for (const type of shownSectionTypes) {
      void loadSection(type);
    }
  }, [loadSection, shownSectionTypes]);

  // The instances a template generated are read-only context for the Templated section: the term
  // produced them, but they are not themselves editable — the template is.
  const templatedPrompts = sections[MonitoringPromptTermAssociationType.Templated]?.prompts;
  React.useEffect(() => {
    const templates = (templatedPrompts ?? []).filter((prompt) => prompt.type === MonitoringPromptType.Template);
    if (!templates.length) {
      setInstancesByTemplateKey({});
      return;
    }

    let active = true;

    void (async () => {
      const entries = await Promise.all(
        templates.map(async (template) => {
          const templateKey = String(template.key || '');
          try {
            const result = await api.governance.monitoringPrompts.list(
              { page: 0, itemsPerPage: PROMPTS_PER_SECTION },
              {
                status: NON_PREVIOUS_PROMPT_STATUSES,
                type: [MonitoringPromptType.Instance],
                templatePromptKey: templateKey,
              }
            );
            return [templateKey, result.items || []] as const;
          } catch {
            // A failed instance listing is context we could not load, not a failure of the section
            // above it — the association list stays usable and simply shows no instances.
            return [templateKey, []] as const;
          }
        })
      );

      if (active) {
        setInstancesByTemplateKey(Object.fromEntries(entries));
      }
    })();

    return () => {
      active = false;
    };
  }, [api, templatedPrompts]);

  const applyAssociation = React.useCallback(
    async (type: MonitoringPromptTermAssociationType, promptIds: InternalId[], operation: MonitoringPromptTermAssociationOperation) => {
      const results = await api.governance.monitoringPrompts.bulkTerms({
        termIds: [termId as InternalId],
        promptIds,
        associationType: type,
        operation,
      });

      await loadSection(type);
      setSection(type, { message: summarizeAssociationResults(results, operation) });
    },
    [api, loadSection, setSection, termId]
  );

  const removePrompt = React.useCallback(
    async (type: MonitoringPromptTermAssociationType, prompt: ClientMonitoringPromptEntity) => {
      const promptId = String(prompt.id);
      setSection(type, { busyPromptId: promptId, error: undefined, message: undefined });

      try {
        await applyAssociation(type, [promptId as InternalId], MonitoringPromptTermAssociationOperation.Remove);
      } catch (requestError) {
        setSection(type, { error: (requestError as Error)?.message || 'Failed to remove the association.' });
      } finally {
        setSection(type, { busyPromptId: undefined });
      }
    },
    [applyAssociation, setSection]
  );

  const confirmPicker = React.useCallback(
    async (promptIds: InternalId[]) => {
      if (!pickerType) {
        return;
      }

      setPickerSaving(true);
      setPickerError(undefined);

      try {
        await applyAssociation(pickerType, promptIds, MonitoringPromptTermAssociationOperation.Add);
        setPickerType(undefined);
      } catch (requestError) {
        setPickerError((requestError as Error)?.message || 'Failed to add the association.');
      } finally {
        setPickerSaving(false);
      }
    },
    [applyAssociation, pickerType]
  );

  if (!termKey || !termId) {
    return <div className="text-sm text-slate-500">This term has to be saved before prompts can be associated with it.</div>;
  }

  return (
    <div className="space-y-6">
      {shownSections.map((section) => {
        const state = sections[section.type] ?? EMPTY_SECTION;

        return (
          <section key={section.type} className="rounded-xl border border-slate-200 bg-white">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-slate-900">{section.label}</h3>
                <p className="mt-1 text-sm text-slate-500">{section.description}</p>
                {section.rewritesRunHistory ? (
                  <p className="mt-1 text-xs text-amber-700">
                    Changing this rewrites the term assignments on runs that already happened, so statistics filtered by this term move with
                    it.
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => {
                  setPickerError(undefined);
                  setPickerType(section.type);
                }}
                className={`${BP_PRIMARY_BUTTON_CLASSES} shrink-0`}
              >
                Add prompts
              </button>
            </div>

            <div className="px-5 py-4">
              {state.loading ? <div className="text-sm text-slate-500">Loading…</div> : null}
              {state.error ? <div className="text-sm text-rose-600">{state.error}</div> : null}
              {state.message ? <div className="mb-3 text-sm text-emerald-700">{state.message}</div> : null}

              {!state.loading && !state.prompts.length ? (
                <div className="text-sm text-slate-500">No prompts use this term here yet.</div>
              ) : null}

              {!state.loading && state.prompts.length ? (
                <ul className="divide-y divide-slate-100">
                  {state.prompts.map((prompt) => {
                    const promptId = String(prompt.id);
                    const templateKey = prompt.templatePrompt?.getStableKey?.();
                    const instances = instancesByTemplateKey[String(prompt.key || '')] ?? [];

                    return (
                      <li key={promptId} className="py-3">
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold text-slate-900">{prompt.name}</span>
                              <span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600">
                                {SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('MonitoringPromptStatus', prompt.status)}
                              </span>
                              <span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600">
                                {SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('MonitoringPromptType', prompt.type)}
                              </span>
                            </div>

                            <div className="mt-2 flex flex-wrap gap-2">
                              {(prompt.engines || []).map((engine) => (
                                <TaxonomyTermNameBadge
                                  key={engine.getStableKey()}
                                  api={api}
                                  termKey={engine.getStableKey()}
                                  applyFacetPresentation
                                  termFacet={TaxonomyFacet.Engine}
                                />
                              ))}
                            </div>

                            {templateKey ? <div className="mt-2 text-xs text-slate-500">Generated from template {templateKey}</div> : null}

                            {section.type === MonitoringPromptTermAssociationType.Templated && instances.length ? (
                              <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2">
                                <div className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">
                                  Generated instances ({instances.length})
                                </div>
                                <ul className="mt-1 space-y-0.5">
                                  {instances.map((instance) => (
                                    <li key={String(instance.id)} className="truncate text-xs text-slate-600">
                                      {instance.name}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : null}
                          </div>

                          <button
                            type="button"
                            disabled={state.busyPromptId === promptId}
                            onClick={() => {
                              void removePrompt(section.type, prompt);
                            }}
                            className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                          >
                            {state.busyPromptId === promptId ? 'Removing…' : 'Remove'}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          </section>
        );
      })}

      {pickerType ? (
        <MonitoringPromptPickerModal
          api={api}
          show
          title={`Add prompts · ${associationSection(pickerType).label}`}
          intro={
            <>
              <strong>{term.name}</strong> will be added to the {associationSection(pickerType).label.toLowerCase()} terms of every prompt
              you select.
              {associationSection(pickerType).rewritesRunHistory ? ' Runs that already happened are updated to match.' : null}
              {pickerType === MonitoringPromptTermAssociationType.Templated && termFacet
                ? ` Only templates that iterate the ${SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue(
                    'TaxonomyFacet',
                    termFacet
                  )} facet are listed: a template substitutes one facet into one placeholder.`
                : null}
            </>
          }
          eligibility={associationPromptEligibility(pickerType, termFacet)}
          alreadySelectedPromptIds={(sections[pickerType]?.prompts ?? []).map((prompt) => String(prompt.id))}
          saving={pickerSaving}
          error={pickerError}
          onHide={() => setPickerType(undefined)}
          onConfirm={(promptIds) => {
            void confirmPicker(promptIds);
          }}
        />
      ) : null}
    </div>
  );
}
